import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ChannelId } from '@app/app.constants';
import { ScreenService } from '@core/services/screen/screen.service';
import { merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ScreenTypeComponent } from '../screen-type/screen-type.component';

@Component({
  standalone: false,
  selector: 'app-screen-information',
  templateUrl: './screen-information.component.html',
  styleUrls: ['./screen-information.component.scss'],
})
export class ScreenInformationComponent implements OnInit, OnDestroy {
  @Input() screenForm: FormGroup;
  @Input() screenPriceLib: [];
  @Input() screenTypes: [];
  @Input() selectedType = [];
  @Input() screenDurCalMax;
  @Input() individualTypes;
  @Input() screenTypeToDelete;
  @Input() screenTypeToInsert;
  @Input() screenInClosureDate;
  @Input() data;
  durationRegExcessError = false;
  durationNatExcessError = false;
  loading = false;
  forcableIsMixte = false;
  isRegList = false;
  isRegOffers = false;
  isRegSpecial = false;
  isNatChannels = false;
  isPrimeOffers = false;
  libFormated: { display_input: string; display_label: string }[] = [];
  screenHourTheoForm: FormGroup;
  screenHourForm: FormGroup;
  private componentDestroyed$: Subject<void> = new Subject();

  readonly forcableChoices: { display: string; id: string }[] = [
    { display: 'Mixte', id: 'Mixte' },
    { display: 'Non Forçable', id: 'N' },
    { display: 'Forçable', id: 'Y' },
  ];

  constructor(public screenService: ScreenService, public dialog: MatDialog, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
    this.setDefaultValue();
    this.formatScreenPriceLib();
    this.setScreenHourTheo();
    this.checkChannelType();
    this.initDurMaxNatReg();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  initForm(): void {
    this.screenHourTheoForm = this.fb.group({
      screenTheoHour: [
        this.screenForm?.value?.screenTheoHour ? this.screenForm.value.screenTheoHour : null,
        [Validators.required, Validators.maxLength(5)],
      ],
    });

    this.screenHourForm = this.fb.group({
      screenHour: [
        this.screenForm?.value?.screenHour ? this.screenForm.value.screenHour : null,
        [Validators.required, Validators.maxLength(5)],
      ],
    });
  }

  initDurMaxNatReg() {
    if (this.data.screenTransfer) {
      this.screenForm.get('screenDurMax').setValue('');
      this.screenForm.get('screenDurOuv').setValue('');
    }
    if (this.isRegOffers || this.isPrimeOffers) {
      if (this.data.isCreating) {
        merge(this.screenForm.get('screenDurMaxNat').valueChanges, this.screenForm.get('screenDurMaxReg').valueChanges)
          .pipe(debounceTime(500), takeUntil(this.componentDestroyed$))
          .subscribe(() => {
            this.screenForm
              .get('screenDurCalMax')
              .setValue(Number(this.screenForm.get('screenDurMaxNat').value) + Number(this.screenForm.get('screenDurMaxReg').value));
          });
      } else {
        let screenNat = this.data.screenSelected.find(screenNat => screenNat.screenRegion === 'REG NAT');
        let screenReg = this.data.screenSelected.find(screenReg => screenReg.screenRegion !== 'REG NAT');

        if (screenNat && screenReg) {
          this.screenForm.get('screenDurMaxNat').setValue(screenNat.screenDurMax);
          this.screenForm.get('screenDurMaxReg').setValue(screenReg.screenDurMax);

          this.screenForm.get('screenDurCalMax').setValue(Number(screenNat.screenDurMax) + Number(screenReg.screenDurMax));
        }

        this.screenForm
          .get('screenDurMaxNat')
          .valueChanges.pipe(debounceTime(500), takeUntil(this.componentDestroyed$))
          .subscribe(
            () => {
              if (Number(this.screenForm.get('screenDurMaxNat').value) >= screenNat.screenDurSold) {
                this.screenForm.get('screenDurMaxNat').setValue(this.screenForm.get('screenDurMaxNat').value, { emitEvent: false });
                this.screenForm
                  .get('screenDurCalMax')
                  .setValue(Number(this.screenForm.get('screenDurMaxNat').value) + Number(this.screenForm.get('screenDurMaxReg').value));
                this.durationNatExcessError = false;
              } else {
                this.screenForm.get('screenDurMaxNat')?.setErrors({ invalid: true });
                this.durationNatExcessError = true;
              }
            },
            (error: Error) => {
              console.error(error);
            }
          );

        this.screenForm
          .get('screenDurMaxReg')
          .valueChanges.pipe(debounceTime(500), takeUntil(this.componentDestroyed$))
          .subscribe(
            () => {
              const maxDurRegSold = Math.max(
                ...this.data.screenSelected
                  .filter(screenReg => screenReg.screenRegion !== 'REG NAT')
                  .map(screenReg => screenReg.screenDurSold)
              );
              if (Number(this.screenForm.get('screenDurMaxReg').value) >= maxDurRegSold) {
                this.screenForm.get('screenDurMaxReg').setValue(this.screenForm.get('screenDurMaxReg').value, { emitEvent: false });
                this.screenForm
                  .get('screenDurCalMax')
                  .setValue(Number(this.screenForm.get('screenDurMaxNat').value) + Number(this.screenForm.get('screenDurMaxReg').value));
                this.durationRegExcessError = false;
              } else {
                this.screenForm.get('screenDurMaxReg')?.setErrors({ invalid: true });
                this.durationRegExcessError = true;
              }
            },
            (error: Error) => {
              console.error(error);
            }
          );
      }

      this.screenForm
        .get('screenDurCalMax')
        .valueChanges.pipe(takeUntil(this.componentDestroyed$))
        .subscribe(
          () => {
            if (this.screenForm.get('screenDurCalMax').value > this.screenDurCalMax) {
              this.screenForm.get('screenDurCalMax')?.setErrors({ invalid: true });
              this.screenForm.get('screenDurCalMax')?.markAsTouched();
            }
          },
          (error: Error) => {
            console.error(error);
          }
        );
    }
  }

  checkChannelType() {
    if (
      this.data.screenChannel[0].channelCode !== '3' ||
      (this.data.screenChannel[0].channelCode === '3' &&
        this.data.screenChannel[0].regionCode === 'NAT' &&
        this.data.screenChannel[0].isRegion === false)
    ) {
      this.isNatChannels = true;
    } else if (
      this.data.screenChannel[0].id === ChannelId.NAT_REG &&
      this.data.screenChannel[0].channelCode === '3' &&
      this.data.screenChannel[0].isRegion === true
    ) {
      this.isRegOffers = true;
    } else if (
      this.data.screenChannel[0].id === ChannelId.SO_PRIME &&
      this.data.screenChannel[0].channelCode === '3' &&
      this.data.screenChannel[0].isRegion === true
    ) {
      this.isPrimeOffers = true;
    } else if (
      this.data.screenChannel[0].id === ChannelId.NOA ||
      (this.data.screenChannel[0].id === ChannelId.VIA_STELLA &&
        this.data.screenChannel[0].channelCode === '3' &&
        this.data.screenChannel[0].isRegion === true)
    ) {
      this.isRegSpecial = true;
    } else {
      this.isRegList = true;
    }
  }

  setDefaultValue(): void {
    if (this.data.isCreating) {
      this.screenForm.get('screenForcable').setValue('N', { emitEvent: false });
    }
    if (this.screenForm.get('screenForcable').value === 'Mixte') {
      this.forcableIsMixte = true;
    }
  }

  setScreenHourTheo(): void {
    merge(this.screenHourForm.get('screenHour').valueChanges, this.screenHourTheoForm.get('screenTheoHour').valueChanges)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        () => {
          this.screenForm.get('screenHour').setValue(this.screenHourForm.get('screenHour').value);
          this.screenForm.get('screenTheoHour').setValue(this.screenHourTheoForm.get('screenTheoHour').value);
        },
        (error: Error) => {
          console.error(error);
        }
      );
  }

  formatScreenPriceLib(): void {
    this.screenPriceLib.forEach((element: string) => {
      this.libFormated = [
        ...this.libFormated,
        { display_label: this.capitalizeFirstLetter(element.replace('_', ' ')), display_input: element },
      ];
    });
  }

  capitalizeFirstLetter(string): any {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  openTypeDialog() {
    const dialogRef = this.dialog.open(ScreenTypeComponent, {
      data: {
        screenType: this.screenTypes,
        selectedType: this.selectedType,
        screenTypeToInsert: this.screenTypeToInsert,
        screenTypeToDelete: this.screenTypeToDelete,
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(selectedType => {
        if (this.data.isCreating) {
          this.selectedType = selectedType;
        }
      });
  }

  removeScreenType(type): void {
    this.selectedType.forEach(element => {
      if (type === element) {
        const indexType = this.selectedType.indexOf(element);
        element.isSelected = false;
        this.selectedType.splice(indexType, 1);
      }
    });

    this.individualTypes.forEach(element => {
      if (type === element) {
        const indexType = this.individualTypes.indexOf(element);
        element.isSelected = false;
        this.individualTypes.splice(indexType, 1);
      }
    });

    this.screenTypeToDelete.push(type);
  }

  /**
   * Select forcable when updating screen
   * @param choice
   */
  selectedForcable(choice): void {
    this.screenForm.get('screenForcable').setValue(choice.id);
  }

  /**
   * Select forcable when creating screen
   * @param event
   */
  selectedScreenForcable(event: MatSlideToggleChange): void {
    if (event.checked) {
      this.screenForm.get('screenForcable').setValue('Y');
    } else {
      this.screenForm.get('screenForcable').setValue('N');
    }
  }
}
